import { useMemo } from 'react';
import styles from './markdown.module.css';

const BOLD_DELIMITER_AND_NO_OTHER_EMPHASIS = '**';
const HEADING_LINE = /^(#{1,2})\s+(.+)$/;
const BULLET_LINE = /^-\s+(.+)$/;

interface InlineRun {
  text: string;
  bold: boolean;
}

type DocumentBlock =
  | { kind: 'heading'; sourceLevel: 1 | 2; runs: InlineRun[] }
  | { kind: 'bullets'; items: InlineRun[][] }
  | { kind: 'paragraph'; runs: InlineRun[] };

export function Markdown({
  source,
  topHeadingLevel = 1,
}: {
  source: string;
  topHeadingLevel?: 1 | 2;
}): React.ReactElement {
  const blocks = useMemo(() => parseDocument(source), [source]);

  return (
    <div className={styles.prose}>
      {blocks.map((block, index) => (
        <RenderedBlock key={index} block={block} topHeadingLevel={topHeadingLevel} />
      ))}
    </div>
  );
}

function RenderedBlock({
  block,
  topHeadingLevel,
}: {
  block: DocumentBlock;
  topHeadingLevel: 1 | 2;
}): React.ReactElement {
  if (block.kind === 'heading') {
    const level = Math.min(block.sourceLevel + topHeadingLevel - 1, 6);
    const HeadingTag = `h${level}` as 'h1' | 'h2' | 'h3';

    return (
      <HeadingTag className={block.sourceLevel === 1 ? styles.documentTitle : styles.section}>
        <InlineRuns runs={block.runs} />
      </HeadingTag>
    );
  }

  if (block.kind === 'bullets') {
    return (
      <ul className={styles.bullets}>
        {block.items.map((item, index) => (
          <li key={index}>
            <InlineRuns runs={item} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p className={styles.paragraph}>
      <InlineRuns runs={block.runs} />
    </p>
  );
}

function InlineRuns({ runs }: { runs: readonly InlineRun[] }): React.ReactElement {
  return (
    <>
      {runs.map((run, index) =>
        run.bold ? <strong key={index}>{run.text}</strong> : <span key={index}>{run.text}</span>,
      )}
    </>
  );
}

function splitIntoBoldAndPlainRuns(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  let bold = false;

  for (const piece of text.split(BOLD_DELIMITER_AND_NO_OTHER_EMPHASIS)) {
    if (piece !== '') runs.push({ text: piece, bold });
    bold = !bold;
  }

  return runs;
}

function parseDocument(source: string): DocumentBlock[] {
  const blocks: DocumentBlock[] = [];
  let hardWrappedParagraphLines: string[] = [];
  let bulletItems: string[] = [];

  function closeParagraph(): void {
    if (hardWrappedParagraphLines.length === 0) return;
    blocks.push({
      kind: 'paragraph',
      runs: splitIntoBoldAndPlainRuns(hardWrappedParagraphLines.join(' ')),
    });
    hardWrappedParagraphLines = [];
  }

  function closeBullets(): void {
    if (bulletItems.length === 0) return;
    blocks.push({ kind: 'bullets', items: bulletItems.map(splitIntoBoldAndPlainRuns) });
    bulletItems = [];
  }

  function closeEverythingOpen(): void {
    closeParagraph();
    closeBullets();
  }

  for (const line of source.split('\n')) {
    const trimmed = line.trim();

    if (trimmed === '') {
      closeEverythingOpen();
      continue;
    }

    const heading = HEADING_LINE.exec(trimmed);
    if (heading?.[1] && heading[2]) {
      closeEverythingOpen();
      blocks.push({
        kind: 'heading',
        sourceLevel: heading[1].length === 1 ? 1 : 2,
        runs: splitIntoBoldAndPlainRuns(heading[2]),
      });
      continue;
    }

    const bullet = BULLET_LINE.exec(trimmed);
    if (bullet?.[1]) {
      closeParagraph();
      bulletItems.push(bullet[1]);
      continue;
    }

    const wrapsThePreviousBullet = bulletItems.length > 0 && line.startsWith(' ');
    if (wrapsThePreviousBullet) {
      bulletItems[bulletItems.length - 1] += ` ${trimmed}`;
      continue;
    }

    closeBullets();
    hardWrappedParagraphLines.push(trimmed);
  }

  closeEverythingOpen();
  return blocks;
}
